import { createContext, useContext, useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { authService, tokenManager } from '../services/authApi'
import { extractUserFromJWT, isTokenExpired } from '../utils/jwtDecoder'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userRole, setUserRole] = useState(null) // "Administrator" | "BuildingManager" | null
  const [managedBuildings, setManagedBuildings] = useState([])
  const [selectedBuildingId, setSelectedBuildingIdState] = useState(
    () => localStorage.getItem('selectedBuildingId') || null
  )

  useEffect(() => {
    checkAuthStatus()
  }, [])

  const setSelectedBuilding = (buildingId) => {
    setSelectedBuildingIdState(buildingId)
    if (buildingId) {
      localStorage.setItem('selectedBuildingId', buildingId)
    } else {
      localStorage.removeItem('selectedBuildingId')
    }
  }

  const checkAuthStatus = async () => {
    try {
      const token = tokenManager.getToken()

      if (token) {
        // Check if token is expired
        if (isTokenExpired(token)) {
          console.log('Token expired, attempting refresh...')
          const refreshSuccess = await refreshToken()
          if (!refreshSuccess) {
            logout()
            return
          }
          // Get the new token after refresh
          const newToken = tokenManager.getToken()
          if (newToken) {
            const userFromJWT = extractUserFromJWT(newToken)
            if (userFromJWT) {
              setUser(userFromJWT)
              setIsAuthenticated(true)
              // Update stored profile
              tokenManager.setUserProfile(userFromJWT)
            }
          }
        } else {
          // Token is valid, extract user data from JWT
          const userFromJWT = extractUserFromJWT(token)
          if (userFromJWT) {
            setUser(userFromJWT)
            setIsAuthenticated(true)
            // Update stored profile
            tokenManager.setUserProfile(userFromJWT)
            // Set Sentry user context
            Sentry.setUser({ id: userFromJWT.userId, email: userFromJWT.email, username: userFromJWT.firstName })
          } else {
            // Invalid token structure
            logout()
          }
        }

        // Restore role and buildings from localStorage
        const storedRole = localStorage.getItem('userRole')
        const storedBuildings = localStorage.getItem('managedBuildings')
        if (storedRole) {
          setUserRole(storedRole)
        }
        if (storedBuildings) {
          try {
            const buildings = JSON.parse(storedBuildings)
            setManagedBuildings(buildings)
            // Auto-select first building if none selected
            if (!localStorage.getItem('selectedBuildingId') && buildings.length > 0) {
              setSelectedBuilding(buildings[0].buildingId)
            }
          } catch (e) {
            // ignore parse errors
          }
        }

        // Fetch fresh data from /me endpoint
        try {
          const { default: api } = await import('../services/api')
          const meResponse = await api.get('/auth/me')
          const meData = meResponse.data
          if (meData.role) {
            setUserRole(meData.role)
            localStorage.setItem('userRole', meData.role)
          }
          if (meData.managedBuildings) {
            setManagedBuildings(meData.managedBuildings)
            localStorage.setItem('managedBuildings', JSON.stringify(meData.managedBuildings))
            if (!localStorage.getItem('selectedBuildingId') && meData.managedBuildings.length > 0) {
              setSelectedBuilding(meData.managedBuildings[0].buildingId)
            }
          }
        } catch (e) {
          // /me endpoint may fail if token was just refreshed, use cached data
          console.log('Could not fetch /me endpoint, using cached role data')
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      logout()
    } finally {
      setLoading(false)
    }
  }

  const processLoginResponse = (responseData) => {
    const { token, refreshToken, access_token, refresh_token, role, managedBuildings: buildings } = responseData

    // Handle different possible token field names
    const accessToken = token || access_token
    const refToken = refreshToken || refresh_token

    if (!accessToken) {
      throw new Error('No token received from server')
    }

    tokenManager.setTokens(accessToken, refToken)

    const userFromJWT = extractUserFromJWT(accessToken)
    if (!userFromJWT) {
      throw new Error('Could not extract user data from token')
    }

    tokenManager.setUserProfile(userFromJWT)
    setUser(userFromJWT)
    setIsAuthenticated(true)

    // Set Sentry user context
    Sentry.setUser({ id: userFromJWT.userId, email: userFromJWT.email, username: userFromJWT.firstName })

    // Store role and managed buildings
    if (role) {
      setUserRole(role)
      localStorage.setItem('userRole', role)
    }
    if (buildings) {
      setManagedBuildings(buildings)
      localStorage.setItem('managedBuildings', JSON.stringify(buildings))
      if (buildings.length > 0) {
        setSelectedBuilding(buildings[0].buildingId)
      }
    }

    return { success: true, data: responseData, user: userFromJWT }
  }

  const extractErrorMessage = (error, fallback) => {
    const data = error.response?.data
    const msg = typeof data === 'string' ? data
      : data?.message || data?.detail || data?.title
      || error.message
    return msg || fallback
  }

  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials)
      return processLoginResponse(response.data)
    } catch (error) {
      console.error('Login failed:', error)
      return { success: false, error: extractErrorMessage(error, 'Login failed') }
    }
  }

  const loginWithGoogle = async (credential) => {
    try {
      const response = await authService.externalLogin({ provider: 'Google', idToken: credential })
      return processLoginResponse(response.data)
    } catch (error) {
      console.error('Google login failed:', error)
      return { success: false, error: extractErrorMessage(error, 'Google login failed') }
    }
  }

  const logout = () => {
    tokenManager.clearTokens()
    localStorage.removeItem('userRole')
    localStorage.removeItem('managedBuildings')
    localStorage.removeItem('selectedBuildingId')
    Sentry.setUser(null)
    setUser(null)
    setIsAuthenticated(false)
    setUserRole(null)
    setManagedBuildings([])
    setSelectedBuildingIdState(null)
  }

  const refreshToken = async () => {
    try {
      const currentToken = tokenManager.getToken()
      const currentRefreshToken = tokenManager.getRefreshToken()

      if (!currentRefreshToken) {
        logout()
        return false
      }

      const response = await authService.refreshToken({
        token: currentToken,
        refreshToken: currentRefreshToken
      })

      const { token, refreshToken: newRefreshToken } = response.data
      tokenManager.setTokens(token, newRefreshToken)
      return true
    } catch (error) {
      console.error('Token refresh failed:', error)
      logout()
      return false
    }
  }

  const restoreDevSession = (sessionData) => {
    const { token, refreshToken: refToken, role, managedBuildings: buildings } = sessionData
    if (!token) return false

    tokenManager.setTokens(token, refToken)
    const userFromJWT = extractUserFromJWT(token)
    if (!userFromJWT) return false

    tokenManager.setUserProfile(userFromJWT)
    setUser(userFromJWT)
    setIsAuthenticated(true)
    if (role) {
      setUserRole(role)
      localStorage.setItem('userRole', role)
    }
    if (buildings) {
      setManagedBuildings(buildings)
      localStorage.setItem('managedBuildings', JSON.stringify(buildings))
      if (buildings.length > 0 && !localStorage.getItem('selectedBuildingId')) {
        setSelectedBuilding(buildings[0].buildingId)
      }
    }
    return true
  }

  const isAdmin = userRole === 'Administrator'
  const isBuildingManager = userRole === 'BuildingManager'

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    loginWithGoogle,
    logout,
    refreshToken,
    checkAuthStatus,
    restoreDevSession,
    userRole,
    isAdmin,
    isBuildingManager,
    managedBuildings,
    selectedBuildingId,
    setSelectedBuilding
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
